import { useMemo } from 'react';
import type { DataGridSortingState } from '@ibsys/kit';
import type { AllDetailsSortKey, AllDetailsSortState } from '@/entities/dashboards';
import { BLOCK_COLUMN_PREFIX } from './useAllDetailsColumns';

type Params = {
  sort: AllDetailsSortState | null;
  onSortChange: (next: AllDetailsSortState | null) => void;
};

/**
 * Маппинг состояния сортировки между моделью страницы (`AllDetailsSortState`)
 * и состоянием DataGrid (`DataGridSortingState`). Возвращает текущее значение
 * для грида и обработчик его изменений, преобразующий обратно в серверную модель.
 */
export const useAllDetailsSorting = ({ sort, onSortChange }: Params) => {
  const sortingForGrid = useMemo<DataGridSortingState>(() => {
    if (!sort) return [];
    if (sort.key === 'blockRating') {
      if (typeof sort.blockId !== 'number') return [];
      return [{ columnId: `${BLOCK_COLUMN_PREFIX}${sort.blockId}`, direction: sort.direction }];
    }
    const columnIdByKey: Record<Exclude<AllDetailsSortKey, 'blockRating'>, string> = {
      evaluationDate: 'date',
      consultantName: 'consultant',
      clientName: 'clientName',
      externalId: 'externalId',
      totalRating: 'totalScore',
    };
    const columnId = columnIdByKey[sort.key];
    return columnId ? [{ columnId, direction: sort.direction }] : [];
  }, [sort]);

  const handleSortingChange = (next: DataGridSortingState) => {
    if (next.length === 0) {
      onSortChange(null);
      return;
    }
    const { columnId, direction } = next[0];
    if (columnId.startsWith(BLOCK_COLUMN_PREFIX)) {
      const blockId = Number(columnId.slice(BLOCK_COLUMN_PREFIX.length));
      if (!Number.isFinite(blockId)) {
        onSortChange(null);
        return;
      }
      onSortChange({ key: 'blockRating', direction, blockId });
      return;
    }
    const keyByColumnId: Record<string, AllDetailsSortKey> = {
      date: 'evaluationDate',
      consultant: 'consultantName',
      clientName: 'clientName',
      externalId: 'externalId',
      totalScore: 'totalRating',
    };
    const key = keyByColumnId[columnId];
    if (!key) {
      onSortChange(null);
      return;
    }
    onSortChange({ key, direction });
  };

  return { sortingForGrid, handleSortingChange };
};
